import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { 
  ENTITY_TYPES, 
  COMMERCIAL_STATUS, 
  CLIENT_STATUS, 
  STATUS_LABELS, 
  STATUS_COLORS,
  obtenirEntites,
  mettreAJourStatutEntite,
  obtenirStatistiquesPipeline,
  convertirProspectEnClient,
  initialiserSystemeUnifie
} from '../services/unifiedClientService';
import { Search, Plus, Filter, Users, UserCheck, UserX, TrendingUp, Calendar, FileText, RefreshCw } from 'lucide-react';
import AddProspectModal from './AddProspectModal';
import { marquerClientsExistants, verifierEtatClients } from '../utils/markExistingClients';
import { BeneficiaryDocumentsManager } from './beneficiaryDocuments';

/**
 * Pipeline unifié de gestion prospects/clients
 * Affiche toutes les entités avec leur statut et permet une transition transparente
 */

const UnifiedClientPipeline = () => {
  const [entites, setEntites] = useState([]);
  const [statistiques, setStatistiques] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [marquageEnCours, setMarquageEnCours] = useState(false);
  const [infoMarquage, setInfoMarquage] = useState(null);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
  const [filtres, setFiltres] = useState({
    typeEntite: 'all', // 'all', 'prospect', 'client'
    statutCommercial: 'all',
    statutClient: 'all',
    recherche: ''
  });

  // Charger les entités
  const chargerEntites = async () => {
    try {
      setLoading(true);
      
      // Construire les filtres pour Firestore
      const filtresFirestore = {};
      if (filtres.typeEntite !== 'all') {
        filtresFirestore.typeEntite = filtres.typeEntite;
      }
      if (filtres.statutCommercial !== 'all') {
        filtresFirestore.statutCommercial = filtres.statutCommercial;
      }
      if (filtres.statutClient !== 'all') {
        filtresFirestore.statutClient = filtres.statutClient;
      }

      const entitesData = await obtenirEntites(db, filtresFirestore);
      
      // Appliquer le filtre de recherche côté client (compatible avec la structure existante)
      let entitesFiltrees = entitesData;
      if (filtres.recherche) {
        const terme = filtres.recherche.toLowerCase();
        entitesFiltrees = entitesData.filter(entite => 
          entite.lastName?.toLowerCase().includes(terme) ||
          entite.firstName?.toLowerCase().includes(terme) ||
          entite.email?.toLowerCase().includes(terme) ||
          entite.nom?.toLowerCase().includes(terme) || // Compatibilité ascendante
          entite.prenom?.toLowerCase().includes(terme) // Compatibilité ascendante
        );
      }

      setEntites(entitesFiltrees);
      
      // Charger les statistiques (toujours sur toutes les entités pour avoir une vue complète)
      const stats = await obtenirStatistiquesPipeline(db);
      setStatistiques(stats);
      
      setLoading(false);
    } catch (err) {
      console.error('Erreur chargement entités:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Mettre à jour le statut d'une entité
  const handleMettreAJourStatut = async (entiteId, nouveauStatut) => {
    try {
      await mettreAJourStatutEntite(db, entiteId, nouveauStatut, {
        utilisateur: 'utilisateur_interface'
      });
      await chargerEntites(); // Recharger les données
    } catch (err) {
      console.error('Erreur mise à jour statut:', err);
      setError(err.message);
    }
  };

  // Convertir prospect en client
  const handleConvertirEnClient = async (entiteId, devisId) => {
    if (window.confirm('Êtes-vous sûr de vouloir convertir ce prospect en client ? Cette action est irréversible.')) {
      try {
        await convertirProspectEnClient(db, entiteId, devisId, {
          utilisateur: 'utilisateur_interface'
        });
        await chargerEntites(); // Recharger les données
      } catch (err) {
        console.error('Erreur conversion prospect:', err);
        setError(err.message);
      }
    }
  };

  // Initialiser le système au montage
  useEffect(() => {
    const initialiser = async () => {
      try {
        await initialiserSystemeUnifie(db);
        
        // Vérifier l'état des clients
        const etatClients = await verifierEtatClients();
        setInfoMarquage(etatClients);
        
        await chargerEntites();
      } catch (err) {
        console.error('Erreur initialisation système:', err);
        setError('Erreur lors de l\'initialisation du système');
      }
    };
    initialiser();
  }, []);

  // Recharger quand les filtres changent
  useEffect(() => {
    if (!loading) {
      chargerEntites();
    }
  }, [filtres.typeEntite, filtres.statutCommercial, filtres.statutClient]);

  // Marquer automatiquement les clients existants
  const handleMarquageClients = async () => {
    try {
      setMarquageEnCours(true);
      const resultat = await marquerClientsExistants();
      setInfoMarquage(resultat);
      
      // Recharger les entités
      await chargerEntites();
      
      console.log('✅ Marquage des clients terminé avec succès');
    } catch (err) {
      console.error('Erreur marquage clients:', err);
      setError('Erreur lors du marquage des clients existants');
    } finally {
      setMarquageEnCours(false);
    }
  };

  // Obtenir le statut d'affichage pour une entité
  const getStatutAffichage = (entite) => {
    if (entite.typeEntite === ENTITY_TYPES.PROSPECT) {
      return {
        label: STATUS_LABELS[entite.statutCommercial],
        couleur: STATUS_COLORS[entite.statutCommercial],
        type: 'commercial'
      };
    } else {
      return {
        label: STATUS_LABELS[entite.statutClient],
        couleur: STATUS_COLORS[entite.statutClient],
        type: 'client'
      };
    }
  };

  // Composant de statistiques
  const StatistiquesPipeline = () => {
    if (!statistiques) return null;

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm text-center">
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{statistiques.total}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Total</div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm text-center">
          <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{statistiques.prospects}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Prospects</div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm text-center">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">{statistiques.clients}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Bénéficiaires</div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm text-center">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">{statistiques.tauxConversion}%</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Taux conversion</div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm text-center">
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{statistiques.contact}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Contact</div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm text-center">
          <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{statistiques.devisEnvoye}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Devis envoyé</div>
        </div>
      </div>
    );
  };

  // Composant de filtre
  const FiltresPipeline = () => (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm mb-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        {/* Recherche */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
          <input
            type="text"
            placeholder="Rechercher par nom, prénom ou email..."
            value={filtres.recherche}
            onChange={(e) => setFiltres(prev => ({ ...prev, recherche: e.target.value }))}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          />
        </div>

        {/* Actions et filtres */}
        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-800 flex items-center"
          >
            <Plus size={16} className="mr-2" />
            Nouveau Prospect
          </button>

          {infoMarquage && infoMarquage.sansTypeEntite > 0 && (
            <button
              onClick={handleMarquageClients}
              disabled={marquageEnCours}
              className="px-4 py-2 bg-green-600 dark:bg-green-700 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-800 disabled:bg-gray-300 dark:disabled:bg-gray-600 flex items-center"
            >
              {marquageEnCours ? (
                <>
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                  Marquage...
                </>
              ) : (
                <>
                  <RefreshCw size={16} className="mr-2" />
                  Marquer {infoMarquage.sansTypeEntite} clients
                </>
              )}
            </button>
          )}

          <select
            value={filtres.typeEntite}
            onChange={(e) => setFiltres(prev => ({ ...prev, typeEntite: e.target.value }))}
            className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">Tous les types</option>
            <option value={ENTITY_TYPES.PROSPECT}>Prospects</option>
            <option value={ENTITY_TYPES.CLIENT}>Clients</option>
          </select>

          <select
            value={filtres.statutCommercial}
            onChange={(e) => setFiltres(prev => ({ ...prev, statutCommercial: e.target.value }))}
            className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">Tous statuts commerciaux</option>
            {Object.values(COMMERCIAL_STATUS).map(statut => (
              <option key={statut} value={statut}>{STATUS_LABELS[statut]}</option>
            ))}
          </select>

          <select
            value={filtres.statutClient}
            onChange={(e) => setFiltres(prev => ({ ...prev, statutClient: e.target.value }))}
            className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">Tous statuts bénéficiaires</option>
            {Object.values(CLIENT_STATUS).map(statut => (
              <option key={statut} value={statut}>{STATUS_LABELS[statut]}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );

  // Composant d'une carte entité
  const CarteEntite = ({ entite }) => {
    const statut = getStatutAffichage(entite);
    const estProspect = entite.typeEntite === ENTITY_TYPES.PROSPECT;

    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-4 border-l-4 ${
        estProspect ? 'border-yellow-400 dark:border-yellow-500' : 'border-green-400 dark:border-green-500'
      }`}>
        <div className="flex justify-between items-start mb-3">
        <div>
            <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
              {entite.firstName || entite.prenom} {entite.lastName || entite.nom}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {entite.email} • {entite.phoneNumber || entite.telephone}
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className={`px-2 py-1 text-xs rounded-full font-medium ${statut.couleur}`}>
              {statut.label}
            </span>
            <span className={`px-2 py-1 text-xs rounded-full ${
              estProspect ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
            }`}>
              {estProspect ? 'Prospect' : 'Bénéficiaire'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700 dark:text-gray-300">
          <div>
            <strong className="text-gray-900 dark:text-gray-100">Création:</strong> {entite.dateCreation?.toLocaleDateString('fr-FR')}
          </div>
          {entite.dateConversion && (
            <div>
              <strong className="text-gray-900 dark:text-gray-100">Conversion:</strong> {entite.dateConversion?.toLocaleDateString('fr-FR')}
            </div>
          )}
          {(entite.address || entite.adresse) && (
            <div className="md:col-span-2">
              <strong className="text-gray-900 dark:text-gray-100">Adresse:</strong> {(() => {
                // Gérer les différents formats d'adresse
                const adresse = entite.address || entite.adresse;
                
                // Si c'est un objet, formater l'adresse
                if (typeof adresse === 'object' && adresse !== null) {
                  const { street, city, postalCode, country } = adresse;
                  const parties = [];
                  if (street) parties.push(street);
                  if (city) parties.push(city);
                  if (postalCode) parties.push(postalCode);
                  if (country) parties.push(country);
                  return parties.join(', ');
                }
                
                // Si c'est une chaîne, l'afficher directement
                return adresse;
              })()}
              {entite.addressNumber && ` (${entite.addressNumber})`}
            </div>
          )}
          {entite.notes && (
            <div className="md:col-span-2">
              <strong className="text-gray-900 dark:text-gray-100">Notes:</strong> {entite.notes}
            </div>
          )}
          <div>
            <strong className="text-gray-900 dark:text-gray-100">Statut:</strong> {entite.isActive ? 'Actif' : 'Inactif'}
          </div>
          {entite.color && (
            <div>
              <strong className="text-gray-900 dark:text-gray-100">Couleur:</strong>
              <span
                className="inline-block w-4 h-4 rounded-full ml-2 border border-gray-300 dark:border-gray-600"
                style={{ backgroundColor: entite.color }}
              ></span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
          {estProspect ? (
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Pipeline commercial:</span>
              {Object.values(COMMERCIAL_STATUS).map(statut => (
                <button
                  key={statut}
                  onClick={() => handleMettreAJourStatut(entite.id, statut)}
                  disabled={entite.statutCommercial === statut}
                  className={`px-2 py-1 text-xs rounded ${
                    entite.statutCommercial === statut
                      ? 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {STATUS_LABELS[statut].charAt(0)}
                </button>
              ))}

              {entite.statutCommercial === COMMERCIAL_STATUS.SIGNE && (
                <button
                  onClick={() => handleConvertirEnClient(entite.id, 'devis_accepte')}
                  className="px-3 py-1 bg-green-600 dark:bg-green-700 text-white text-xs rounded hover:bg-green-700 dark:hover:bg-green-800"
                >
                  Convertir en client
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Statut client:</span>
                {Object.values(CLIENT_STATUS).map(statut => (
                  <button
                    key={statut}
                    onClick={() => handleMettreAJourStatut(entite.id, statut)}
                    disabled={entite.statutClient === statut}
                    className={`px-2 py-1 text-xs rounded ${
                      entite.statutClient === statut
                        ? 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400 cursor-not-allowed'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {STATUS_LABELS[statut]}
                  </button>
                ))}
              </div>

              {/* Bouton Documents pour les bénéficiaires */}
              <button
                onClick={() => {
                  setSelectedBeneficiary(entite);
                  setShowDocumentsModal(true);
                }}
                className="px-3 py-1.5 bg-blue-600 dark:bg-blue-700 text-white text-sm rounded hover:bg-blue-700 dark:hover:bg-blue-800 flex items-center gap-2"
              >
                <FileText size={16} />
                Gérer les documents
              </button>
            </div>
          )}
        </div>

        {/* Historique (aperçu) */}
        {entite.historique && entite.historique.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <details className="text-sm">
              <summary className="cursor-pointer text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
                Historique ({entite.historique.length} événements)
              </summary>
              <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                {entite.historique.slice(-3).map((event, index) => (
                  <div key={index} className="text-xs text-gray-600 dark:text-gray-400">
                    • {event.date?.toDate().toLocaleDateString('fr-FR')} - {event.details}
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Chargement du pipeline...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* En-tête */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Pipeline Prospects/Bénéficiaires</h1>
          <p className="text-gray-600 dark:text-gray-400">Gestion unifiée du cycle de vente et de la relation bénéficiaire</p>
        </div>

        {/* Statistiques */}
        <StatistiquesPipeline />

        {/* Filtres */}
        <FiltresPipeline />

        {/* Message d'erreur */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <button
              onClick={chargerEntites}
              className="mt-2 px-3 py-1 bg-red-600 dark:bg-red-700 text-white text-sm rounded hover:bg-red-700 dark:hover:bg-red-800"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Liste des entités */}
        <div>
          {entites.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
              <Users size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {filtres.recherche ? 'Aucune entité trouvée' : 'Aucune entité à afficher'}
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {filtres.recherche
                  ? 'Aucune entité ne correspond à votre recherche.'
                  : 'Commencez par ajouter votre premier prospect ou client.'
                }
              </p>
            </div>
          ) : (
            <div>
              <div className="flex justify-between items-center mb-4">
                <p className="text-gray-600 dark:text-gray-400">
                  {entites.length} entité{entites.length > 1 ? 's' : ''} trouvée{entites.length > 1 ? 's' : ''}
                </p>
                <button
                  onClick={chargerEntites}
                  className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Actualiser
                </button>
              </div>

              <div className="space-y-4">
                {entites.map(entite => (
                  <CarteEntite key={entite.id} entite={entite} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal d'ajout de prospect */}
      <AddProspectModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onProspectAdded={chargerEntites}
      />

      {/* Modal de gestion des documents */}
      {showDocumentsModal && selectedBeneficiary && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowDocumentsModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header du modal */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-white dark:bg-gray-800">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Documents - {selectedBeneficiary.firstName || selectedBeneficiary.prenom} {selectedBeneficiary.lastName || selectedBeneficiary.nom}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedBeneficiary.email}
                </p>
              </div>
              <button
                onClick={() => setShowDocumentsModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                ×
              </button>
            </div>

            {/* Body du modal avec le gestionnaire de documents */}
            <div className="flex-1 overflow-hidden">
              <BeneficiaryDocumentsManager
                db={db}
                user={{ uid: 'admin', role: 'admin' }}
                beneficiaryId={selectedBeneficiary.id}
                isClientView={false}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedClientPipeline;
